import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Building2, Users, Globe2, ShieldCheck, TrendingUp, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatNumber = (n) => {
  if (n == null) return '—';
  if (n >= 1000) return new Intl.NumberFormat('en-GB').format(n);
  return String(n);
};

const TrustStrip = () => {
  const [stats, setStats] = useState(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/stats`).then(r => {
      if (!cancelled) setStats(r.data);
    }).catch(() => { /* silent — strip just hides */ });
    return () => { cancelled = true; };
  }, []);

  // Build pills only from numbers we actually have
  const pills = useMemo(() => {
    if (!stats) return [];
    const items = [];
    if (stats.properties) items.push({
      icon: <Building2 className="h-3.5 w-3.5" />,
      value: formatNumber(stats.properties),
      label: 'live listings',
      tone: 'gold',
    });
    if (stats.users) items.push({
      icon: <Users className="h-3.5 w-3.5" />,
      value: formatNumber(stats.users),
      label: 'members on the platform',
      tone: 'emerald',
    });
    if (stats.countries) items.push({
      icon: <Globe2 className="h-3.5 w-3.5" />,
      // Public surface intentionally shows ONLY the count — never the list of
      // country codes. Reason: dirty rows in legacy data ("INDIA" vs "IN", etc.)
      // can leak through to consumers and create misleading-claim risk.
      // Admins see the truthful list at /admin?tab=vision (AdminVisionTargets).
      value: stats.countries,
      label: 'live markets',
      tone: 'sky',
    });
    if (stats.harvested_properties) items.push({
      icon: <Sparkles className="h-3.5 w-3.5" />,
      value: formatNumber(stats.harvested_properties),
      label: 'AI-matched homes',
      tone: 'amber',
    });
    if (stats.verified_users != null) items.push({
      icon: <ShieldCheck className="h-3.5 w-3.5" />,
      // Honesty rule: only render when ≥ 1 to avoid the awkward "0 Verified Partners".
      // Below the floor we hide the pill entirely.
      value: stats.verified_users,
      label: 'Verified Partners',
      tone: 'gold',
      _hideIfZero: true,
    });
    items.push({
      icon: <TrendingUp className="h-3.5 w-3.5" />,
      value: 'GBP · INR · CAD',
      label: 'currencies auto-detected',
      tone: 'emerald',
    });
    return items.filter(p => !(p._hideIfZero && (!p.value || Number(p.value) <= 0)));
  }, [stats]);

  // Rotate the highlighted pill every 4s
  useEffect(() => {
    if (pills.length < 2) return;
    const t = setInterval(() => setActive(i => (i + 1) % pills.length), 4000);
    return () => clearInterval(t);
  }, [pills.length]);

  if (!stats || pills.length === 0) return null;

  return (
    <div
      className="w-full"
      style={{ background: 'linear-gradient(90deg, #0A0A0C 0%, #1F2937 50%, #0A0A0C 100%)' }}
      data-testid="trust-strip"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-white/80">
        <span
          className="text-[9px] sm:text-[10px] tracking-[0.3em] uppercase font-bold flex-shrink-0"
          style={{ color: '#D4AF37' }}
        >
          Trusted Real Estate Network
        </span>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 justify-center">
          {pills.map((p, idx) => {
            const isActive = idx === active;
            const toneColor = isActive
              ? '#D4AF37'
              : 'rgba(255,255,255,0.55)';
            return (
              <span
                key={p.label}
                data-testid={`trust-pill-${idx}`}
                className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-medium transition-colors duration-500"
                style={{ color: toneColor }}
              >
                <span className="opacity-80">{p.icon}</span>
                <span className="font-bold">{p.value}</span>
                <span className="opacity-90">{p.label}</span>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TrustStrip;
